import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, getDoc, query, orderBy, where, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import './ManageDepositHistory.css';

interface DepositHistory {
  id: string;
  userId: string;
  amount: number;
  timestamp: Timestamp;
  status: 'pending' | 'completed' | 'canceled';
  newDeposit?: number;
  oldDeposit?: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
}

interface DepositStats {
  totalAmount: number;
  pendingAmount: number;
  completedAmount: number;
  canceledAmount: number;
  pendingCount: number;
  completedCount: number;
  canceledCount: number;
  totalCount: number;
}

const ManageDepositHistory: React.FC = () => {
  const [deposits, setDeposits] = useState<DepositHistory[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredDeposits, setFilteredDeposits] = useState<DepositHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Editing states
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editValues, setEditValues] = useState<{ [key: string]: any }>({});
  
  // Stats
  const [stats, setStats] = useState<DepositStats>({
    totalAmount: 0,
    pendingAmount: 0,
    completedAmount: 0,
    canceledAmount: 0,
    pendingCount: 0,
    completedCount: 0,
    canceledCount: 0,
    totalCount: 0
  });

  const fetchUsers = async () => {
    try {
      console.log('Bắt đầu tải danh sách nhà cung cấp...');
      
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'provider')
      );
      const querySnapshot = await getDocs(usersQuery);
      const usersData: User[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Provider doc:', doc.id, data);
        
        usersData.push({
          id: doc.id,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || data.phoneNumber || '',
          role: data.role || ''
        });
      });

      console.log(`Đã tải ${usersData.length} nhà cung cấp`);
      setUsers(usersData);
      
    } catch (error) {
      console.error('Lỗi khi tải danh sách nhà cung cấp:', error);
      setUsers([]);
    }
  };

  const fetchDepositHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải lịch sử nạp tiền...');

      let depositsQuery;
      let querySnapshot;

      try {
        // Server-side constraints to reduce payload
        const constraints: any[] = [];
        if (selectedUser) constraints.push(where('userId', '==', selectedUser));
        if (selectedStatus) constraints.push(where('status', '==', selectedStatus));
        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          constraints.push(where('timestamp', '>=', Timestamp.fromDate(start)));
        }
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          constraints.push(where('timestamp', '<=', Timestamp.fromDate(end)));
        }
        try {
          depositsQuery = query(
            collection(db, 'deposit_history'),
            ...constraints,
            orderBy('timestamp', 'desc')
          );
          console.log('Thử query deposit_history có orderBy và constraints...');
          querySnapshot = await getDocs(depositsQuery);
        } catch (indexError) {
          console.warn('Query deposit_history với orderBy thất bại, thử query không order:', indexError);
          depositsQuery = query(
            collection(db, 'deposit_history'),
            ...constraints
          );
          console.log('Thử query deposit_history không order...');
          querySnapshot = await getDocs(depositsQuery);
        }
      } catch (qErr) {
        console.warn('Xây dựng query deposit_history có điều kiện thất bại, fallback lấy tất cả:', qErr);
        depositsQuery = collection(db, 'deposit_history');
        querySnapshot = await getDocs(depositsQuery);
      }

      const depositsData: DepositHistory[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Deposit doc:', doc.id, data);
        
        depositsData.push({
          id: doc.id,
          userId: data.userId || '',
          amount: data.amount || 0,
          timestamp: data.timestamp || new Date(),
          status: data.status || 'pending',
          newDeposit: data.newDeposit || 0,
          oldDeposit: data.oldDeposit || 0
        });
      });

      // Sắp xếp thủ công theo timestamp nếu cần
      if (depositsData.length > 0) {
        depositsData.sort((a, b) => {
          const aTime = a.timestamp?.toDate?.() || new Date();
          const bTime = b.timestamp?.toDate?.() || new Date();
          return bTime.getTime() - aTime.getTime();
        });
      }

      console.log(`Đã tải ${depositsData.length} bản ghi lịch sử nạp tiền`);
      setDeposits(depositsData);
      
    } catch (error) {
      console.error('Lỗi khi tải lịch sử nạp tiền:', error);
      setError('Không thể tải dữ liệu lịch sử nạp tiền. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users) map[u.id] = u.name || '';
    return map;
  }, [users]);

  const userEmailMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users) map[u.id] = u.email || '';
    return map;
  }, [users]);

  const getUserName = (userId: string): string => {
    return userNameMap[userId] || `User ID: ${userId}`;
  };

  const getUserEmail = (userId: string): string => {
    return userEmailMap[userId] || '';
  };

  const applyFilters = (depositsToFilter: DepositHistory[]) => {
    let filtered = [...depositsToFilter];

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(deposit => {
        const userName = getUserName(deposit.userId).toLowerCase();
        const userEmail = getUserEmail(deposit.userId).toLowerCase();
        const amount = deposit.amount.toString();
        const status = deposit.status.toLowerCase();
        
        return userName.includes(searchLower) ||
               userEmail.includes(searchLower) ||
               amount.includes(searchLower) ||
               status.includes(searchLower) ||
               deposit.id.toLowerCase().includes(searchLower);
      });
    }

    // User filter
    if (selectedUser) {
      filtered = filtered.filter(deposit => deposit.userId === selectedUser);
    }

    // Status filter
    if (selectedStatus) {
      filtered = filtered.filter(deposit => deposit.status === selectedStatus);
    }

    // Date range filter
    if (startDate || endDate) {
      filtered = filtered.filter(deposit => {
        const depositDate = deposit.timestamp?.toDate?.() || new Date();
        const depositDateStr = depositDate.toISOString().split('T')[0];
        
        if (startDate && depositDateStr < startDate) return false;
        if (endDate && depositDateStr > endDate) return false;
        
        return true;
      });
    }

    return filtered;
  };

  const calculateStats = (depositsToCalculate: DepositHistory[]) => {
    const newStats: DepositStats = {
      totalAmount: 0,
      pendingAmount: 0,
      completedAmount: 0,
      canceledAmount: 0,
      pendingCount: 0,
      completedCount: 0,
      canceledCount: 0,
      totalCount: depositsToCalculate.length
    };

    depositsToCalculate.forEach(deposit => {
      newStats.totalAmount += deposit.amount;
      
      if (deposit.status === 'pending') {
        newStats.pendingAmount += deposit.amount;
        newStats.pendingCount++;
      } else if (deposit.status === 'completed') {
        newStats.completedAmount += deposit.amount;
        newStats.completedCount++;
      } else if (deposit.status === 'canceled') {
        newStats.canceledAmount += deposit.amount;
        newStats.canceledCount++;
      }
    });

    setStats(newStats);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (timestamp: Timestamp): string => {
    try {
      const date = timestamp?.toDate?.() || new Date();
      return new Intl.DateTimeFormat('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Lỗi khi format ngày:', error);
      return 'Không xác định';
    }
  };

  const getRowClass = (status: DepositHistory['status']): string => {
    switch (status) {
      case 'pending':
        return 'row-pending';
      case 'completed':
        return 'row-completed';
      case 'canceled':
        return 'row-canceled';
      default:
        return '';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedUser('');
    setSelectedStatus('');
    setStartDate('');
    setEndDate('');
  };

  const startEditing = (depositId: string, field: string, currentValue: any) => {
    setEditingCell({ id: depositId, field });
    setEditValues({ [depositId + '_' + field]: currentValue });
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValues({});
  };

  const saveEdit = async (depositId: string, field: string) => {
    try {
      const editKey = depositId + '_' + field;
      const newValue = editValues[editKey];
      
      // If no change or empty value, just cancel
      if (newValue === undefined || newValue === null || newValue === '') {
        cancelEditing();
        return;
      }

      // Get original value for comparison
      const originalDeposit = deposits.find(d => d.id === depositId);
      if (!originalDeposit) {
        cancelEditing();
        return;
      }

      const originalValue = originalDeposit[field as keyof DepositHistory];

      // Check if value actually changed
      if (field === 'amount' && parseFloat(newValue) === originalValue) {
        cancelEditing();
        return;
      }
      
      if (field === 'status' && newValue === originalValue) {
        cancelEditing();
        return;
      }

      if (field === 'oldDeposit' && parseFloat(newValue) === originalValue) {
        cancelEditing();
        return;
      }

      if (field === 'newDeposit' && parseFloat(newValue) === originalValue) {
        cancelEditing();
        return;
      }

      // Validate amount fields
      if (field === 'amount' || field === 'oldDeposit' || field === 'newDeposit') {
        const amount = parseFloat(newValue);
        if (isNaN(amount) || amount < 0) {
          alert('Số tiền phải là số không âm hợp lệ');
          return; // Don't cancel editing on validation error
        }
      }

      // Validate status
      if (field === 'status') {
        if (!['pending', 'completed', 'canceled'].includes(newValue)) {
          alert('Trạng thái không hợp lệ');
          return; // Don't cancel editing on validation error
        }
      }

      // Update in Firestore
      const depositRef = doc(db, 'deposit_history', depositId);
      const updateData: any = {};
      
      if (field === 'amount') {
        updateData.amount = parseFloat(newValue);
      } else if (field === 'status') {
        updateData.status = newValue;
      } else if (field === 'oldDeposit') {
        updateData.oldDeposit = parseFloat(newValue);
      } else if (field === 'newDeposit') {
        updateData.newDeposit = parseFloat(newValue);
      }
      
      updateData.updatedAt = new Date();

      await updateDoc(depositRef, updateData);

      // Auto-activate corresponding user and accumulate deposit when marking completed
      if (field === 'status' && newValue === 'completed' && originalDeposit.status !== 'completed') {
        const userIdToUpdate = originalDeposit.userId;
        if (userIdToUpdate) {
          try {
            const userRef = doc(db, 'users', userIdToUpdate);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data() as { status?: string; deposit?: number };
              const currentStatus = userData.status;
              const currentDepositBalance = typeof userData.deposit === 'number' ? userData.deposit : 0;
              const newDepositBalance = currentDepositBalance + (originalDeposit.amount || 0);

              const userUpdate: any = { deposit: newDepositBalance, updatedAt: new Date() };
              if (currentStatus !== 'active') {
                userUpdate.status = 'active';
              }

              await updateDoc(userRef, userUpdate);

              // Optionally reflect balances in the deposit history record
              try {
                await updateDoc(depositRef, {
                  oldDeposit: currentDepositBalance,
                  newDeposit: newDepositBalance,
                  updatedAt: new Date()
                });
              } catch (e2) {
                console.error('Lỗi khi cập nhật old/new deposit cho lịch sử nạp tiền:', e2);
              }
            }
          } catch (e) {
            console.error('Lỗi khi cập nhật số dư ký quỹ hoặc kích hoạt người dùng:', e);
          }
        }
      }

      // When changing from completed to other status, subtract the deposit and auto-inactivate if below minimum
      if (field === 'status' && newValue !== 'completed' && originalDeposit.status === 'completed') {
        const userIdToUpdate = originalDeposit.userId;
        if (userIdToUpdate) {
          try {
            const userRef = doc(db, 'users', userIdToUpdate);
            const settingsRef = doc(db, 'settings', 'app');
            const [userSnap, settingsSnap] = await Promise.all([getDoc(userRef), getDoc(settingsRef)]);

            if (userSnap.exists()) {
              const userData = userSnap.data() as { status?: string; deposit?: number };
              const currentStatus = userData.status;
              const currentDepositBalance = typeof userData.deposit === 'number' ? userData.deposit : 0;
              const subtractAmount = originalDeposit.amount || 0;
              const newDepositBalance = Math.max(0, currentDepositBalance - subtractAmount);

              let minDepositAmount = 0;
              if (settingsSnap.exists()) {
                const s = settingsSnap.data() as { minDepositAmount?: number };
                minDepositAmount = typeof s.minDepositAmount === 'number' ? s.minDepositAmount : 0;
              }

              const userUpdate: any = { deposit: newDepositBalance, updatedAt: new Date() };
              if (newDepositBalance < minDepositAmount && currentStatus !== 'inactive') {
                userUpdate.status = 'inactive';
              }

              await updateDoc(userRef, userUpdate);

              // Reflect new balances in the deposit history record
              try {
                await updateDoc(depositRef, {
                  oldDeposit: currentDepositBalance,
                  newDeposit: newDepositBalance,
                  updatedAt: new Date()
                });
              } catch (e2) {
                console.error('Lỗi khi cập nhật old/new deposit cho lịch sử nạp tiền (revert):', e2);
              }
            }
          } catch (e) {
            console.error('Lỗi khi trừ ký quỹ hoặc cập nhật trạng thái người dùng:', e);
          }
        }
      }

      // Update local state
      setDeposits(prevDeposits =>
        prevDeposits.map(deposit =>
          deposit.id === depositId
            ? { 
                ...deposit, 
                [field]: ['amount', 'oldDeposit', 'newDeposit'].includes(field) ? parseFloat(newValue) : newValue 
              }
            : deposit
        )
      );

      cancelEditing();
      console.log(`Đã cập nhật ${field} cho deposit ${depositId}`);
      
    } catch (error) {
      console.error('Lỗi khi cập nhật:', error);
      alert('Không thể cập nhật. Vui lòng thử lại.');
      // Don't cancel editing on error so user can try again
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, depositId: string, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit(depositId, field);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchUsers(),
        fetchDepositHistory()
      ]);
    };
    fetchData();
  }, []);

  // Refetch when server-side filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchDepositHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, selectedStatus, startDate, endDate]);

  // Memoize filters and pagination
  const memoFilteredDeposits = useMemo(() => applyFilters(deposits), [deposits, users, searchTerm, selectedUser, selectedStatus, startDate, endDate]);

  const totalPages = useMemo(() => Math.ceil(memoFilteredDeposits.length / recordsPerPage) || 1, [memoFilteredDeposits.length]);
  const displayedDeposits = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return memoFilteredDeposits.slice(start, start + recordsPerPage);
  }, [memoFilteredDeposits, currentPage]);

  useEffect(() => {
    setFilteredDeposits(memoFilteredDeposits);
    if (currentPage > totalPages) setCurrentPage(1);
    calculateStats(memoFilteredDeposits);
  }, [memoFilteredDeposits, currentPage, totalPages]);

  if (loading) {
    return (
      <div className="manage-deposit-history">
        <div className="page-header">
          <h2>Quản lý lịch sử nạp tiền</h2>
          <p className="page-subtitle">Quản lý và theo dõi lịch sử nạp tiền của người dùng</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-deposit-history">
      <div className="page-header">
        <h2>Quản lý lịch sử nạp tiền</h2>
        <p className="page-subtitle">Quản lý và theo dõi lịch sử nạp tiền của người dùng</p>
        <p className="edit-instruction">💡 Click vào Số tiền/Trạng thái hoặc icon ✏️ để chỉnh sửa. Nhấn Enter để lưu, Escape để hủy.</p>
      </div>

      {/* Stats Summary */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats.totalAmount)}</div>
          <div className="stat-label">Tổng tiền nạp</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{formatCurrency(stats.pendingAmount)}</div>
          <div className="stat-label">Đang chờ ({stats.pendingCount})</div>
        </div>
        <div className="stat-card completed">
          <div className="stat-value">{formatCurrency(stats.completedAmount)}</div>
          <div className="stat-label">Đã hoàn thành ({stats.completedCount})</div>
        </div>
        <div className="stat-card canceled">
          <div className="stat-value">{formatCurrency(stats.canceledAmount)}</div>
          <div className="stat-label">Đã hủy ({stats.canceledCount})</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalCount}</div>
          <div className="stat-label">Tổng giao dịch</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="search-term">Tìm kiếm:</label>
          <input
            id="search-term"
            type="text"
            placeholder="Tên nhà cung cấp, email, số tiền, trạng thái..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="user-filter">Nhà cung cấp:</label>
          <select
            id="user-filter"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <option value="">Tất cả nhà cung cấp</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Trạng thái:</label>
          <select
            id="status-filter"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">Tất cả trạng thái</option>
            <option value="pending">Đang chờ</option>
            <option value="completed">Đã hoàn thành</option>
            <option value="canceled">Đã hủy</option>
          </select>
        </div>

        <div className="date-filter-row">
          <div className="filter-group">
            <label htmlFor="start-date">Từ ngày:</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date">Đến ngày:</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearFilters} className="clear-filters-btn">
            Xóa bộ lọc
          </button>
          <button onClick={() => {
            fetchUsers();
            fetchDepositHistory();
          }} className="refresh-button">
            Làm mới
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchUsers();
              fetchDepositHistory();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      {/* Deposit History Table */}
      <div className="table-container">
        {displayedDeposits.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data">
              {memoFilteredDeposits.length === 0 ? 'Không có dữ liệu lịch sử nạp tiền' : 'Không có dữ liệu cho trang này'}
            </div>
          </div>
        ) : (
          <table className="deposits-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nhà cung cấp</th>
                <th>Số tiền</th>
                <th>Số dư cũ</th>
                <th>Số dư mới</th>
                <th>Trạng thái</th>
                <th>Thời gian</th>
              </tr>
            </thead>
            <tbody>
              {displayedDeposits.map((deposit, index) => (
                <tr key={deposit.id} className={getRowClass(deposit.status)}>
                  <td className="order-cell">
                    <span className="order-display">{(currentPage - 1) * recordsPerPage + index + 1}</span>
                  </td>
                  <td className="user-info">
                    <div className="user-details">
                      <strong>{getUserName(deposit.userId)}</strong>
                      <small className="user-email">{getUserEmail(deposit.userId)}</small>
                    </div>
                  </td>
                  <td className="amount-cell">
                    {editingCell?.id === deposit.id && editingCell?.field === 'amount' ? (
                      <input
                        type="number"
                        className="edit-input amount-edit"
                        value={editValues[deposit.id + '_amount'] || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          [deposit.id + '_amount']: e.target.value
                        })}
                        onKeyDown={(e) => handleKeyPress(e, deposit.id, 'amount')}
                        onBlur={() => saveEdit(deposit.id, 'amount')}
                        autoFocus
                        min="0"
                        step="1000"
                      />
                    ) : (
                      <div className="editable-container">
                        <span 
                          className="amount-value editable"
                          onClick={() => startEditing(deposit.id, 'amount', deposit.amount)}
                          title="Click để chỉnh sửa"
                        >
                          {formatCurrency(deposit.amount)}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(deposit.id, 'amount', deposit.amount)}
                          title="Chỉnh sửa số tiền"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="old-deposit-cell">
                    {editingCell?.id === deposit.id && editingCell?.field === 'oldDeposit' ? (
                      <input
                        type="number"
                        className="edit-input amount-edit"
                        value={editValues[deposit.id + '_oldDeposit'] || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          [deposit.id + '_oldDeposit']: e.target.value
                        })}
                        onKeyDown={(e) => handleKeyPress(e, deposit.id, 'oldDeposit')}
                        onBlur={() => saveEdit(deposit.id, 'oldDeposit')}
                        autoFocus
                        min="0"
                        step="1000"
                      />
                    ) : (
                      <div className="editable-container">
                        <span 
                          className="amount-value editable"
                          onClick={() => startEditing(deposit.id, 'oldDeposit', deposit.oldDeposit)}
                          title="Click để chỉnh sửa"
                        >
                          {formatCurrency(deposit.oldDeposit || 0)}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(deposit.id, 'oldDeposit', deposit.oldDeposit)}
                          title="Chỉnh sửa số dư cũ"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="new-deposit-cell">
                    {editingCell?.id === deposit.id && editingCell?.field === 'newDeposit' ? (
                      <input
                        type="number"
                        className="edit-input amount-edit"
                        value={editValues[deposit.id + '_newDeposit'] || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          [deposit.id + '_newDeposit']: e.target.value
                        })}
                        onKeyDown={(e) => handleKeyPress(e, deposit.id, 'newDeposit')}
                        onBlur={() => saveEdit(deposit.id, 'newDeposit')}
                        autoFocus
                        min="0"
                        step="1000"
                      />
                    ) : (
                      <div className="editable-container">
                        <span 
                          className="amount-value editable"
                          onClick={() => startEditing(deposit.id, 'newDeposit', deposit.newDeposit)}
                          title="Click để chỉnh sửa"
                        >
                          {formatCurrency(deposit.newDeposit || 0)}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(deposit.id, 'newDeposit', deposit.newDeposit)}
                          title="Chỉnh sửa số dư mới"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="status-cell">
                    {editingCell?.id === deposit.id && editingCell?.field === 'status' ? (
                      <select
                        className="edit-select status-edit"
                        value={editValues[deposit.id + '_status'] || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          [deposit.id + '_status']: e.target.value
                        })}
                        onKeyDown={(e) => handleKeyPress(e, deposit.id, 'status')}
                        onBlur={() => saveEdit(deposit.id, 'status')}
                        autoFocus
                      >
                        <option value="pending">Đang chờ</option>
                        <option value="completed">Đã hoàn thành</option>
                        <option value="canceled">Đã hủy</option>
                      </select>
                    ) : (
                      <div className="editable-container">
                        <span 
                          className={`status-badge ${deposit.status} editable`}
                          onClick={() => startEditing(deposit.id, 'status', deposit.status)}
                          title="Click để chỉnh sửa"
                        >
                          {deposit.status === 'pending' ? 'Đang chờ' : deposit.status === 'completed' ? 'Đã hoàn thành' : 'Đã hủy'}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(deposit.id, 'status', deposit.status)}
                          title="Chỉnh sửa trạng thái"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="timestamp-cell">
                    <span className="timestamp-value">{formatDate(deposit.timestamp)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, memoFilteredDeposits.length)} của {memoFilteredDeposits.length} kết quả
          </div>
          <div className="pagination-controls">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">««</button>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-btn">«</button>
            {(() => {
              const buttons = [] as React.ReactElement[];
              const start = Math.max(1, currentPage - 2);
              const end = Math.min(totalPages, currentPage + 2);
              for (let i = start; i <= end; i++) {
                buttons.push(
                  <button key={i} onClick={() => setCurrentPage(i)} disabled={i === currentPage} className={`pagination-btn ${i === currentPage ? 'active' : ''}`}>{i}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="pagination-btn">»»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDepositHistory; 